import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Observable, combineLatest } from 'rxjs';
import { Select, Store } from '@ngxs/store';
import { AppStateSelectors } from '@appState/app-state.selector';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  TGetCO2Company,
  TGetH2Company,
  TH2FacilityExtendetDTO,
  TLocationDTO,
  VOgeVnb,
} from '@api';
import { Router } from '@angular/router';
import { MapsMarkerService } from '@map/services/maps-marker/maps-marker.service';
import { MapsHelperService } from '@map/services/maps-helper.service';
import { mapOptions } from '@map/map-options/map-options';
import { SetMapIsLoading } from '@pages/map/state/map.actions';
import { LoadFacilityList } from '@appState/app.actions';
import { environment } from '@env';
import { ActionState, ActionStateModel } from '@interfaces';
import { MapStateSelectors } from '@pages/map/state/map-state-selectors.service';
import { DataTableStateSelectors } from '@pages/data-table/state/data-table.state.selectors';
import { AppViewEnum } from '../../../../shared/enums/app-view-enum';
import { GoogleMap } from '@angular/google-maps';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { FileService } from '../../../../shared/services/file.service';
import { MapNetFilterType } from '../../../../shared/enums/map-filter-enum';

@UntilDestroy()
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
})
export class MapComponent implements OnInit, AfterViewInit {
  public readonly environment = environment;
  public readonly appViewEnum = AppViewEnum;
  public readonly mapNetFilterTypes = MapNetFilterType;

  @ViewChild(GoogleMap, { static: false }) map: GoogleMap;

  @Select(AppStateSelectors.getCookiesAreAccepted())
  public readonly cookiesAreAccepted$: Observable<boolean>;

  @Select(AppStateSelectors.getAppActionState())
  public readonly appActionState$: Observable<ActionStateModel>;

  @Select(AppStateSelectors.getUsersCompanyZipCodes())
  public readonly companyZipCodes$: Observable<string[]>;

  @Select(AppStateSelectors.getUsersCompanyLocation())
  public readonly companyLocation$: Observable<TLocationDTO>;

  @Select(AppStateSelectors.getUsersCompany())
  public readonly usersCompany$: Observable<TGetH2Company | TGetCO2Company>;

  @Select(AppStateSelectors.isUserLoggedIn())
  public readonly isUserLoggedIn$: Observable<boolean>;

  @Select(AppStateSelectors.isUserAllowed())
  public readonly isUserAllowed$: Observable<boolean>;

  @Select(AppStateSelectors.isUserOGE())
  public readonly isUserOGE$: Observable<boolean>;

  @Select(AppStateSelectors.isUserVNB())
  public readonly isUserVNB$: Observable<boolean>;

  @Select(AppStateSelectors.getFacilityList())
  public readonly facilityList$: Observable<TH2FacilityExtendetDTO[]>;

  @Select(MapStateSelectors.isLoading)
  public mapIsLoading$: Observable<boolean>;

  @Select(DataTableStateSelectors.isLoadingOGEVNBData())
  public actionStateOGEVNBData$: Observable<ActionState>;

  @Select(DataTableStateSelectors.vnbData())
  public vnbList$: Observable<VOgeVnb[]>;

  @Select(AppStateSelectors.getAppView())
  public appView$: Observable<AppViewEnum>;

  @Select(MapStateSelectors.getActiveDataTypes)
  public activeDataTypes$: Observable<number[]>;

  @Select(MapStateSelectors.getActiveNetTypes)
  public activeNetTypes$: Observable<number[]>;

  @Select(AppStateSelectors.getCompanyGeoJson())
  public geoJsonFile$: Observable<string>;

  public activeDataTypes: number[] = null;
  public activeNetTypes: number[] = null;

  public mapOptions: google.maps.MapOptions = {
    ...mapOptions,
    mapId: environment.mapId,
  };
  public showMap: boolean;

  public get getFacilityListMarkers$() {
    return this.mapsMarkerService.markers$;
  }

  public constructor(
    private readonly mapsMarkerService: MapsMarkerService,
    private readonly store: Store,
    private readonly router: Router,
    private readonly mapsHelperService: MapsHelperService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly fileService: FileService
  ) {}

  public ngOnInit(): void {
    // show loading
    this.store.dispatch(new SetMapIsLoading(true));
    // load facility list for loggedIn users
    combineLatest([this.isUserLoggedIn$, this.isUserOGE$, this.appView$])
      .pipe(untilDestroyed(this))
      .subscribe(([isUserLoggedIn, isUserOGE, appView]) => {
        if (
          isUserLoggedIn &&
          (appView == AppViewEnum.CARBON_DIOXIDE || !isUserOGE)
        ) {
          this.store.dispatch(new LoadFacilityList());
        }
      });

    combineLatest([
      this.cookiesAreAccepted$,
      this.isUserLoggedIn$,
      this.isUserAllowed$,
    ])
      .pipe(untilDestroyed(this))
      .subscribe(
        ([cookiesAreAccepted, isUserLoggedIn, isUserAllowed]: [
          boolean,
          boolean,
          boolean,
        ]) => {
          if (cookiesAreAccepted) {
            this.showMap = true;
            this.changeDetectorRef.detectChanges();
            if (isUserAllowed && isUserLoggedIn) {
              // update map options
              if (isUserAllowed && this.map) {
                this.mapOptions = {
                  ...this.mapOptions,
                  gestureHandling: this.mapsHelperService.getGestureHandling(
                    this.router.url,
                    isUserAllowed
                  ),
                };
                this.addListenerToMap();
              }
            }
          } else {
            this.showMap = false;
          }
          this.store.dispatch(new SetMapIsLoading(false));
        }
      );
  }

  public ngAfterViewInit(): void {
    this.companyLocation$
      .pipe(
        distinctUntilChanged(
          (prev, curr) => prev?.location_id === curr?.location_id
        ),
        untilDestroyed(this)
      )
      .subscribe(companyLocation => {
        if (this.map?.googleMap && this.showMap && companyLocation) {
          // set focus to company location
          this.mapsHelperService.setFocus(this.map.googleMap, {
            lng: companyLocation.location_latitude,
            lat: companyLocation.location_longitude,
            zoom: 11,
          });
        }
      });

    // when dataType, appView or list changed, update marker/cluster
    combineLatest([
      this.activeDataTypes$,
      this.appView$,
      this.facilityList$,
      this.vnbList$,
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([activeDataTypes, appView, facilityList, vnbList]) => {
        let withFitBounds = true;
        if (
          this.activeDataTypes !== null &&
          activeDataTypes !== this.activeDataTypes
        ) {
          withFitBounds = false;
        }
        this.activeDataTypes = activeDataTypes;
        if (this.showMap) {
          if (vnbList && appView == this.appViewEnum.HYDROGEN) {
            this.mapsMarkerService.addVNBMarker(this.map.googleMap, vnbList);
          } else {
            this.mapsMarkerService.addFacilitiesMarker(
              this.map.googleMap,
              facilityList,
              this.activeDataTypes
            );
          }
          this.mapsMarkerService.addMarkerCluster(
            this.map.googleMap,
            this.activeDataTypes,
            withFitBounds
          );
          this.mapsMarkerService.setMarkersVisibility(
            this.activeDataTypes,
            this.map.googleMap
          );
          this.changeDetectorRef.detectChanges();
        }
      });

    combineLatest([this.mapIsLoading$, this.geoJsonFile$, this.activeNetTypes$])
      .pipe(
        untilDestroyed(this),
        map(([mapIsLoading, base64String, activeNetTypes]) => {
          this.activeNetTypes = activeNetTypes;
          if (!mapIsLoading) {
            return this.fileService.decodeGeoJsonFromBase64(base64String);
          }
          return null;
        })
      )
      .subscribe(geoJson => {
        if (!!geoJson && this.map !== undefined) {
          // @TODO STYLING???
          if (this.activeNetTypes.includes(this.mapNetFilterTypes.NET)) {
            // add styling
            this.map.data.setStyle({
              fillColor: '#557897',
              fillOpacity: 1,
              strokeColor: '#557897',
              strokeWeight: 2,
              icon: {
                url: '/assets/geoJson_place.svg',
              },
            });
            this.map.data.addGeoJson(geoJson);
          } else {
            // remove alle feature
            this.map.data.forEach(feature => {
              this.map.data.remove(feature);
            });
          }
        }
      });
  }

  private addListenerToMap(): void {
    this.map?.googleMap?.addListener('zoom_changed', () => {
      this.mapsMarkerService.setMarkersVisibility(
        this.activeDataTypes,
        this.map.googleMap
      );
      this.changeDetectorRef.detectChanges();
    });
  }
}
